<script setup lang="ts">
import type { CalendarDate } from '@ui/components/calendar/picker'
import type { CalendarGetTimeSlots } from '@ui/components/calendar/picker/time/CalendarTimeTypes'
import type { InputMessageValue } from '@ui/components/input/InputMessageTypes'
import type { BackgroundType, MessageVariety, SizesType } from '@ui/styles'
import { CalendarMode } from '@ui/components/calendar/picker'
import Calendar from '@ui/components/calendar/picker/Calendar.vue'
import Divider from '@ui/components/divider/Divider.vue'
import DropdownAdaptive from '@ui/components/dropdown/DropdownAdaptive.vue'
import { useDropdown } from '@ui/components/dropdown/useDropdown'
import Icon from '@ui/components/icon/Icon.vue'
import IconButton from '@ui/components/icon/IconButton.vue'
import { getDateInputText } from '@ui/components/input/date/formatInutDate'
import { ButtonVariant, Sizes } from '@ui/styles'
import { Placements } from '@ui/utils/placements'
import { DVH } from '@ui/utils/supportsCSS'
import { addDays, differenceInDays, parseISO } from 'date-fns'
import { useI18n } from 'vue-i18n'
import ArrowLeft from '~icons/cue-regular/arrow-left'
import ArrowRight from '~icons/cue-regular/arrow-right'
import CalendarIcon from '~icons/cue-regular/calendar'
import Times from '~icons/cue-regular/times'
import Input from '../Input.vue'

const props = withDefaults(
  defineProps<{
    size?: SizesType
    range?: boolean
    weekday?: boolean
    disabled?: boolean
    readonly?: boolean
    target?: CalendarMode
    manual?: boolean
    getSlots?: CalendarGetTimeSlots
    disabledDate?: (date: Date) => boolean
    message?: InputMessageValue
    variety?: MessageVariety
    background?: BackgroundType
    icon?: boolean
    arrows?: boolean
    clearable?: boolean
    placements?: Placements[]
    label?: boolean | string
  }>(),
  {
    size: Sizes.Luke,
    range: false,
    disabled: false,
    readonly: false,
    target: CalendarMode.Days,
    manual: false,
    getSlots: () => [],
    message: '',
    icon: true,
    arrows: false,
    clearable: false,
    placements: () => [Placements.BottomStart, Placements.BottomEnd],
    label: false,
  },
)

const emit = defineEmits(['select:slot', 'cancel'])

const { show, placement, openStatus, open } = useDropdown()
let applied = false
watch(
  show,
  (visible, previous) => {
    if (!visible && previous && !applied) {
      emit('cancel')
    }
    if (visible) {
      applied = false
    }
  },
  { flush: 'sync' },
)

function closeAfterApply(close: () => void) {
  applied = true
  close()
}

const input = ref<InstanceType<typeof Input>>()
const reference = computed(() => input.value?.container.border)

const ignore = [reference]

const model = defineModel<CalendarDate | string>()
const calendarModel = computed({
  get: () => (typeof model.value === 'string' ? parseISO(model.value) : model.value),
  set: (value: CalendarDate | undefined) => {
    model.value = value
  },
})

const DOT_REGEX = /\./g

function removeDots(str: string) {
  return str.replace(DOT_REGEX, '')
}

const i18n = useI18n()
const text = computed(() =>
  removeDots(
    getDateInputText(model.value, {
      range: props.range,
      weekday: props.weekday,
      target: props.target,
      locale: i18n.locale.value,
    }),
  ),
)

function clear() {
  model.value = undefined
}

function onPrevNext(rate: 1 | -1) {
  const value = model.value
  if (value instanceof Date) {
    model.value = addDays(value, rate)
  } else if (value && typeof value !== 'string' && value.start && value.end) {
    const diff = rate * differenceInDays(value.end, value.start)
    value.start = addDays(value.start, diff)
    value.end = addDays(value.end, diff)
  }
}

function onPrev() {
  onPrevNext(-1)
}

function onNext() {
  onPrevNext(1)
}

const placeholder = computed(() => (props.range ? 'orders.export.period' : 'orders.date'))
const label = computed(() => {
  if (props.label) {
    return typeof props.label === 'string' ? props.label : placeholder.value
  }

  return ''
})
</script>

<template lang="pug">
Input(
  ref="input"
  :size="size"
  :readonly="true"
  :disabled="disabled"
  :modelValue="text"
  :open="openStatus"
  :clean="false"
  :iconRight="icon ? CalendarIcon : ''"
  :label="label"
  :placeholder="label ? '' : placeholder"
  :message="message"
  :variety="variety"
  :background="background"
  @focus="!readonly && open()"
  @clear="clear"
)
  template(v-if="clearable" #right)
    .flex.row-12
      Icon.calendar-icon.s-20.flex-shrink-0(v-if="icon" :name="CalendarIcon")
      .flex.row-12(v-if="text && !disabled && !readonly")
        Divider(type="vertical" variant="base")
        IconButton(:name="Times" label="general.reset" :size="Sizes.Leia" :variant="ButtonVariant.Function" @click.stop="clear")
  template(v-if="arrows" #left:part)
    IconButton.arrow(:name="ArrowLeft" :size="Sizes.Leia" :variant="ButtonVariant.FunctionAccent" @click="onPrev")
  template(v-if="arrows" #right:part)
    IconButton.arrow(:name="ArrowRight" :size="Sizes.Leia" :variant="ButtonVariant.FunctionAccent" @click="onNext")
  template(#additional)
    DropdownAdaptive(
      v-model:show="show"
      v-model:placement="placement"
      :mobileBreakpoint="540"
      :maxHeight="`100${DVH}`"
      :padding="false"
      :placements="placements"
      :reference="reference"
      :ignore="ignore"
    )
      template(#default="{ close }")
        Calendar(
          v-model="calendarModel"
          class="lt-sm_m-x-auto"
          :range="range"
          :manual="manual"
          :target="target"
          :getSlots="getSlots"
          :disabledDate="disabledDate"
          @update:modelValue="closeAfterApply(close)"
          @close="close"
          @select:slot="(d, o) => $emit('select:slot', d, o)"
        )
</template>

<style scoped>
.arrow {
  --uno: s-36i p-8;
}

.calendar-icon {
  color: var(--cue-content-neutral-primary);
}
</style>
